package com.example.event_management_system.data.repository

import com.example.event_management_system.data.models.EmailWorkspace
import com.example.event_management_system.data.models.EmailWorkspaceData
import com.example.event_management_system.data.models.WorkspacesDocument
import dev.gitlive.firebase.firestore.FirebaseFirestore
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.map

class EmailWorkspaceRepository(
    private val firestore: FirebaseFirestore
) : IEmailWorkspaceRepository {

    private val docRef = firestore.collection("config").document("emailWorkspaces")

    override fun getWorkspacesFlow(): Flow<List<EmailWorkspace>> {
        return docRef.snapshots.map { snapshot ->
            if (!snapshot.exists) emptyList()
            else snapshot.data<WorkspacesDocument>().toWorkspaceList()
        }
    }

    override suspend fun getWorkspaces(): List<EmailWorkspace> {
        val snapshot = docRef.get()
        return if (!snapshot.exists) emptyList()
        else snapshot.data<WorkspacesDocument>().toWorkspaceList()
    }

    override suspend fun upsertWorkspace(workspace: EmailWorkspace) {
        val current = readDocument()
        val key = workspace.domain.trim().lowercase()
        require(key.isNotBlank()) { "Workspace domain must not be blank." }
        val updated = current.copy(
            workspaces = current.workspaces + (key to EmailWorkspaceData(
                enabled = workspace.enabled,
                fromName = workspace.fromName.trim(),
                secretName = workspace.secretName.trim(),
                secretVersion = workspace.secretVersion.ifBlank { "latest" }
            ))
        )
        docRef.set(updated)
    }

    override suspend fun deleteWorkspace(domain: String) {
        val current = readDocument()
        val key = domain.trim().lowercase()
        if (key !in current.workspaces) return
        val updated = current.copy(workspaces = current.workspaces - key)
        docRef.set(updated)
    }

    override suspend fun setEnabled(domain: String, enabled: Boolean) {
        val current = readDocument()
        val key = domain.trim().lowercase()
        val existing = current.workspaces[key] ?: return
        val updated = current.copy(
            workspaces = current.workspaces + (key to existing.copy(enabled = enabled))
        )
        docRef.set(updated)
    }

    private suspend fun readDocument(): WorkspacesDocument {
        val snapshot = docRef.get()
        return if (snapshot.exists) snapshot.data<WorkspacesDocument>() else WorkspacesDocument()
    }

    private fun WorkspacesDocument.toWorkspaceList(): List<EmailWorkspace> {
        return workspaces.entries
            .map { (domain, data) ->
                EmailWorkspace(
                    domain = domain,
                    enabled = data.enabled,
                    fromName = data.fromName,
                    secretName = data.secretName,
                    secretVersion = data.secretVersion
                )
            }
            .sortedBy { it.domain }
    }
}
